import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import izenuAPI from '../../services/izenuAPI';

// Catálogo → Géneros por rever: tracks without a genre, each with a suggestion
// (classes/GenreSuggestions.php). "Artista" = the artist's other tracks agree;
// "IA" = the most similar-sounding tracks of other artists. The AI gets the
// exact genre first 38% of the time and within its two suggestions 62%, so a
// person listens and confirms — nothing is applied on its own.

const SOURCE_LABEL = { artista: 'Pelo artista', ia: 'IA (som)' };

export default function GenreReview() {
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(30);
    const [genres, setGenres] = useState([]);
    const [other, setOther] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await izenuAPI.get('/admin/music/genreReview.php', { params: { page } });
            if (!data.response) throw new Error(data.message);
            setRows(data.data);
            setTotal(data.total);
            setPerPage(data.per_page || 30);
        } catch (e) {
            setError(e?.response?.data?.message || 'Não foi possível carregar a lista.');
        }
        setLoading(false);
    }, [page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        izenuAPI.get('/admin/genre/fetchAll.php')
            .then(({ data }) => setGenres((data.data || []).map((g) => g.name).filter(Boolean)))
            .catch(() => {});
    }, []);

    const confirm = async (musicId, genre) => {
        if (!genre) return;
        setBusy(musicId);
        try {
            const { data } = await izenuAPI.post('/admin/music/genreReview.php', { music_id: musicId, genre });
            if (!data.response) throw new Error(data.message);
            setRows((prev) => prev.filter((r) => r.music_id !== musicId));
            setTotal((t) => Math.max(0, t - 1));
            window.dispatchEvent(new Event('genrereviewchange'));
        } catch (e) {
            window.alert(e?.response?.data?.message || e.message || 'Não foi possível guardar.');
        }
        setBusy(null);
    };

    const pages = Math.max(1, Math.ceil(total / perPage));

    return (
        <div className="p-6 max-w-6xl">
            <h1 className="text-2xl font-bold text-gray-900">Géneros por rever</h1>
            <p className="text-sm text-gray-600 mt-1 mb-5 max-w-3xl">
                Músicas sem género. Ouve e confirma a sugestão (ou escolhe outro). "Pelo artista" vem das outras músicas do mesmo
                artista; "IA" vem das músicas de outros artistas que soam mais parecido — acerta o género à primeira cerca de 4 vezes
                em 10, por isso vale a pena ouvir antes de confirmar. O título também ajuda (ex.: "Deus", "louvor" → Gospel).
            </p>

            {error && <p className="text-rose-600 mb-4">{error}</p>}
            {loading ? (
                <p className="text-gray-500">A carregar…</p>
            ) : rows.length === 0 ? (
                <p className="text-gray-500">Nada por rever. Todas as músicas têm género.</p>
            ) : (
                <>
                    <p className="text-sm text-gray-500 mb-3">{total} por rever</p>
                    <div className="space-y-2">
                        {rows.map((r) => (
                            <div key={r.music_id} className="flex flex-wrap items-center gap-4 bg-white border rounded-lg p-3">
                                {r.cover
                                    ? <img src={r.cover} alt="" className="w-14 h-14 rounded object-cover bg-gray-100 flex-shrink-0" />
                                    : <div className="w-14 h-14 rounded bg-gray-100 flex-shrink-0" />}
                                <div className="min-w-[200px] flex-1">
                                    <p className="font-medium text-gray-900 truncate">{r.title}</p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {r.artist} · #{r.music_id}
                                        {r.state !== 'approved' && ' · por aprovar'}
                                        {r.release_id && <> · <Link to={`/release/${r.release_id}`} className="text-purple-600 hover:underline">lançamento</Link></>}
                                    </p>
                                    <audio controls preload="none" src={r.audio} className="mt-1 h-8 w-64 max-w-full" />
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    {r.suggestion && (
                                        <button
                                            disabled={busy === r.music_id}
                                            onClick={() => confirm(r.music_id, r.suggestion)}
                                            className="text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded px-3 py-1.5 disabled:opacity-50"
                                            title={`${SOURCE_LABEL[r.source] || ''} · confiança ${Math.round((r.confidence || 0) * 100)}%`}
                                        >
                                            {r.suggestion}
                                            <span className="ml-1 text-xs font-normal opacity-80">· {SOURCE_LABEL[r.source] || ''} {Math.round((r.confidence || 0) * 100)}%</span>
                                        </button>
                                    )}
                                    {r.alternative && (
                                        <button
                                            disabled={busy === r.music_id}
                                            onClick={() => confirm(r.music_id, r.alternative)}
                                            className="text-sm text-teal-700 bg-teal-400/10 border border-teal-500 hover:bg-teal-400/20 rounded px-3 py-1.5 disabled:opacity-50"
                                        >
                                            {r.alternative}
                                        </button>
                                    )}
                                    <select
                                        value={other[r.music_id] || ''}
                                        onChange={(e) => setOther((o) => ({ ...o, [r.music_id]: e.target.value }))}
                                        className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
                                    >
                                        <option value="">Outro…</option>
                                        {genres.map((g) => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                    {other[r.music_id] && (
                                        <button
                                            disabled={busy === r.music_id}
                                            onClick={() => confirm(r.music_id, other[r.music_id])}
                                            className="text-sm text-gray-700 bg-gray-100 border border-gray-300 hover:bg-gray-200 rounded px-3 py-1.5 disabled:opacity-50"
                                        >
                                            Guardar
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {pages > 1 && (
                        <div className="flex items-center gap-3 mt-4 text-sm">
                            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Anterior</button>
                            <span>Página {page} de {pages}</span>
                            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Seguinte</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
